import ResponsiveModal from "@/modules/common/components/ui-kit/modals/responsive-modal";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { DbrEligibility } from "../dtos";

/**
 * §Modals: "Preview DBR" (Figma 12107:60092 / mobile 12107:59580) — the read-only max-loan-per-tenor
 * list behind the credit-feedback amount card's "View DBR" link, which until now was dead text.
 * The design is one tinted panel: a "Maximum loan amount" label, then a row per tenor rendered as
 * "<bold tenor> <amount> EGP" on a single line. No footer, no threshold row, no rate.
 *
 * Numbers come from the SAME enforced eligibility the Credit Feedback step already renders inline
 * through its `dbrCalculator` placement (§Single-source: what's displayed is what submit enforces),
 * so the modal introduces no second source of truth.
 */
const egp = (n: number): string => `${Math.round(n).toLocaleString("en-US")} EGP`;

const DbrSummaryModal = ({
  open,
  onClose,
  eligibility,
  /** the amount the accept-approved-amount path would apply — its tenor row is highlighted */
  approvedAmount,
}: {
  open: boolean;
  onClose: () => void;
  eligibility?: DbrEligibility;
  approvedAmount?: number;
}) => {
  const theme = useTheme();
  const c = theme.nawy.colors.semantic;
  const tenors = eligibility?.tenors ?? [];

  return (
    <ResponsiveModal open={open} onClose={onClose} title="Preview DBR">
      <Box sx={{ p: 2.5, width: { mobile: "100%", desktop: 480 } }}>
        <Box
          sx={{
            p: 2,
            borderRadius: theme.nawy.radius.xl,
            // design Layer/Secondary is #F4F7F8 = primitives.white[200]; the semantic token maps to
            // navy[50] (#EEF3F5), so take the primitive — same surface step-card's cards use.
            backgroundColor: theme.nawy.colors.primitives.white[200],
            maxHeight: "60vh",
            overflowY: "auto",
          }}
        >
          {tenors.length === 0 ? (
            <Typography sx={{ fontSize: 14, lineHeight: "20px", color: c.text.secondary }}>
              No DBR assessment on this step yet.
            </Typography>
          ) : (
            <>
              <Typography sx={{ fontSize: 12, lineHeight: "18px", color: c.text.secondary }}>
                Maximum loan amount
              </Typography>
              <Stack spacing={0.75} sx={{ mt: 1 }}>
                {tenors.map((tenor) => {
                  const chosen =
                    approvedAmount != null &&
                    Math.round(tenor.maxLoan) === Math.round(approvedAmount);
                  return (
                    <Typography
                      key={tenor.tenorYears}
                      sx={{ fontSize: 14, lineHeight: "20px", color: c.text.primary }}
                    >
                      <Box component="span" sx={{ fontWeight: 700 }}>
                        {`${tenor.tenorYears} ${tenor.tenorYears === 1 ? "year" : "years"} `}
                      </Box>
                      <Box
                        component="span"
                        sx={
                          chosen
                            ? { fontWeight: 600, color: c.text.extension.positive }
                            : undefined
                        }
                      >
                        {egp(tenor.maxLoan)}
                      </Box>
                    </Typography>
                  );
                })}
              </Stack>
            </>
          )}
        </Box>
      </Box>
    </ResponsiveModal>
  );
};

export default DbrSummaryModal;
